export class RequiredError extends Error {
  constructor(message: string) {
    super(message)
    this.name = new.target.name
    Error.captureStackTrace?.(this, new.target)
  }
}

type HasValue<T, K extends keyof T> = T & {
  [P in K]-?: NonNullable<T[P]>
}

/** Type guard that checks if a value is non-nullish. */
export const nonNullish = <T>(value: T): value is NonNullable<T> =>
  value !== null && value !== undefined

/*
 * Type guard that checks if an object has a non-nullish value at key K.
 * Useful for narrowing in filters.
 */
export const hasValue =
  <K extends PropertyKey>(key: K) =>
  <T extends { [P in K]?: unknown }>(obj: T): obj is HasValue<T, K> =>
    nonNullish(obj[key])

/** Throws RequiredError if value is nullish. */
export const ensure = <T>(
  value: T | null | undefined,
  message: string
): NonNullable<T> => {
  if (!nonNullish(value)) throw new RequiredError(message)
  return value
}
